import { create } from 'zustand';
import { User } from '@/types';
import { authApi } from '@/services/auth';
import toast from 'react-hot-toast';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  verifyToken: () => Promise<boolean>;
  setUser: (user: User) => void;
  clearError: () => void;
}

// Initialize state from localStorage
const storedToken = localStorage.getItem('token');
const storedUser = localStorage.getItem('user');
const initialUser = storedUser ? JSON.parse(storedUser) : null;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: initialUser,
  token: storedToken,
  isAuthenticated: !!storedToken && !!initialUser,
  isLoading: false,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authApi.login({ email, password });

      if (response.success) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));

        set({
          user: response.user,
          token: response.token,
          isAuthenticated: true,
          isLoading: false,
        });

        toast.success('Successfully logged in!');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      set({ error: errorMessage, isLoading: false });
      toast.error(errorMessage);
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    set({
      user: null,
      token: null,
      isAuthenticated: false,
      error: null,
    });

    toast.success('Logged out successfully');
  },

  verifyToken: async () => {
    const token = get().token;
    if (!token) {
      set({ isAuthenticated: false });
      return false;
    }

    try {
      const response = await authApi.verifyToken();

      if (response.valid) {
        set({
          user: response.user,
          isAuthenticated: true,
        });
        return true;
      } else {
        get().logout();
        return false;
      }
    } catch (error) {
      get().logout();
      return false;
    }
  },

  setUser: (user: User) => {
    set({ user });
    localStorage.setItem('user', JSON.stringify(user));
  },

  clearError: () => {
    set({ error: null });
  },
}));
